import { Heading, Flex, Icon, useMediaQuery, Link, Box, IconButton } from '@chakra-ui/react';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RiMoneyDollarCircleFill } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';

import { IconHamburger, IconQuestionMark, IconSettings } from 'assets/icons';
import Button from 'components/Button';
import { FREETRIAL } from 'global/appConstants';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { useFetchOnBoardingStep } from 'hooks/common/useFetchOnBoardingStep';
import { useUserStore } from 'store/useUserStore';

import AvatarMenu from './AvatarMenu';
import NotificationMenu from './NotificationMenu';
import OnBoardingStep from './OnBoardingStep';
import TrialBanner from './TrialBanner';

export interface IHeaderProps {
  onNavToggle: () => void;
  isNavOpen: boolean;
}

const Header: React.FC<IHeaderProps> = ({ isNavOpen, onNavToggle }) => {
  // const { isOpen: isMileageInsightsOpen, onToggle: onMileageInsightsToggle } = useDisclosure({ defaultIsOpen: false });

  const { t } = useTranslation(['common', 'components']);
  const headerRef = useRef<any>(null);

  const { setHeaderHeight, userStore } = useUserStore(state => ({
    setHeaderHeight: state.setHeaderHeight,
    userStore: state.user as any,
  }));

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isLargerThan768, isLargerthan1280] = useMediaQuery(['(min-width: 768px)', '(min-width: 1280px)'], {
    ssr: false,
  });

  const { data } = useFetchOnBoardingStep({ enabled: false });

  useEffect(() => {
    if (headerRef?.current) {
      const headerHeight = headerRef.current.offsetHeight;
      setHeaderHeight(headerHeight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef, data?.payload?.percent_completed]);

  const getPageTitle = useCallback(() => {
    if (pathname.includes(routePaths.dashboard)) return t('common:home');
    else if (pathname.includes(routePaths.profile)) return t('common:profile');
    else if (pathname.includes(routePaths.whoIsWorking.route)) return t('common:whosWorking');
    else if (pathname.includes(routePaths.employeeTimeClock.route)) return t('common:employeeTimeClock');
    else if (pathname.includes(routePaths.employees.base.route)) return t('common:users');
    else if (pathname.includes(routePaths.jobsAndTasks.base.route)) return t('common:jobs&Tasks');
    else if (pathname.includes(routePaths.messageBlast.base.route)) return t('common:messageBlast');
    else if (pathname.includes(routePaths.timesheetAndMileage.base.route)) return t('common:time&Mileage');
    else if (pathname.includes(routePaths.scheduling.base)) return t('common:scheduling');
    else if (pathname.includes(routePaths.reports.base.route)) return t('common:reports');
    else if (pathname.includes(routePaths.timeOff.base.route)) return t('common:timeOff');
    else if (pathname.includes(routePaths.integrations.base.route)) return t('common:integrations');
    else if (pathname.includes(routePaths.companySettings.base.route)) return t('common:companySettings');
    else if (pathname.includes(routePaths.accountBilling.base.route)) return t('common:account&Billing');
    else if (pathname.includes(routePaths.referAndEarn)) return t('common:referAndEarn');
    else if (pathname.includes(routePaths.notification.base)) return t('common:notifications');
    else if (pathname.includes(routePaths.evv.base.route)) return t('common:evv');
    else return '';
  }, [pathname, t]);

  return (
    <Box as="header" position="sticky" top="0" left="0" right="0" zIndex="docked" ref={headerRef}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        px={{ base: 2, md: 4 }}
        py={{ base: 2, md: 3 }}
        bg="white"
        className="hidden-print"
      >
        <Icon
          as={IconHamburger}
          sx={{ '& path': { fill: 'primary.900' } }}
          cursor="pointer"
          height="28px"
          width="28px"
          position="absolute"
          top={isLargerThan768 ? '35px' : '27px'}
          left="10px"
          transform={isNavOpen ? 'translate(0, -50%)' : 'translate(0, -50%) rotate(180deg)'}
          transition="all 0.2s ease-out"
          onClick={() => onNavToggle()}
        />
        {/* <Flex alignItems="center" ml="9" flexDirection={{ base: 'column', md: 'row' }}> */}
        <Heading as="h2" size="md" mr="6" fontWeight="semibold" ml="9" mb="2">
          {getPageTitle()}
        </Heading>
        {/* </Flex> */}
        <Flex alignItems="center">
          {isLargerthan1280 && (
            <>
              <Link
                href="https://help.timeero.com"
                target="_blank"
                color="white"
                _hover={{ textDecoration: 'none' }}
                mr="2"
              >
                <Button
                  mr="2"
                  size="lg"
                  variant="primary-ghost"
                  sx={{
                    _hover: {
                      path: { fill: 'white', stroke: 'white' },
                    },
                  }}
                  leftIcon={<Icon as={IconQuestionMark} w={5} h={5} />}
                >
                  {t('common:helpCenter')}
                </Button>
              </Link>
              <Box
                color="white"
                _hover={{ textDecoration: 'none' }}
                mr="2"
                onClick={() => navigate(routePaths.referAndEarn)}
              >
                <Button
                  mr="2"
                  size="lg"
                  variant="primary-ghost"
                  sx={{
                    _hover: {
                      color: 'white',
                    },
                  }}
                  leftIcon={<Icon as={RiMoneyDollarCircleFill} w={6} h={6} />}
                >
                  {t('common:referAndEarn')}
                </Button>
              </Box>
            </>
          )}
          {userStore?.userInfo?.roleId === roles.ADMIN ? (
            <IconButton
              w="48px"
              h="48px"
              padding="3"
              mr="2"
              cursor="pointer"
              bg="secondary.50"
              variant="link"
              aria-label="Go to payroll overtime"
              onClick={() => navigate(routePaths.companySettings.general)}
            >
              <IconSettings />
            </IconButton>
          ) : null}
          <NotificationMenu />
          <AvatarMenu />
        </Flex>
        {/* <Collapse in={isMileageInsightsOpen} animateOpacity>
        <MileageInsights />
      </Collapse> */}
      </Flex>

      {/* Show TrialBanner and OnBoardingStep only if:
        - The company is NOT a PIMSY referral (is_pimsy_referral !== 1)
        - The subscription is a free trial
        - The user has an admin role */}
      {userStore?.companyInfo?.is_pimsy_referral !== 1 &&
      userStore?.companyInfo?.subscription === FREETRIAL &&
      userStore?.userInfo?.roleId === roles.ADMIN ? (
        <>
          <TrialBanner />
          <OnBoardingStep />
        </>
      ) : null}
    </Box>
  );
};

export default memo(Header);
